import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { ObserveIcon, FeelIcon, NeedIcon, StarIcon, HeartIcon, InsightIcon } from './BespokeIcons';

/**
 * OFNRProcessHelper - Shows the core OFNR framework
 * Always reminds: Heart connection first, then the process
 */
export const OFNRProcessHelper = ({ isOpen, onClose }) => {
  // When controlled via isOpen/onClose props, don't render trigger
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>

      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-amber-400 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            The ElchmE Process
          </DialogTitle>
        </DialogHeader>

        {/* HEART CONNECTION FIRST - Primary Message */}
        <div className="mb-6 p-4 bg-gradient-to-br from-rose-900/30 to-pink-900/20 rounded-xl border-2 border-rose-500/40">
          <div className="flex items-center gap-3 mb-3">
            <HeartIcon size={28} color="#FB7185" />
            <h3 className="text-rose-300 text-lg font-semibold">Heart Connection First</h3>
          </div>
          <p className="text-white/90 text-base leading-relaxed mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Before anything else, we attempt to form a heart-based connection with the person—no matter the situation.
          </p>
          <p className="text-rose-200/70 text-sm leading-relaxed italic">
            Everything else is gravy to this elemental truth: connection comes first, then understanding, then action.
          </p>
        </div>

        {/* The Engine */}
        <div className="mb-6 p-4 bg-black/30 rounded-lg border border-amber-500/20">
          <p className="text-white/80 text-sm leading-relaxed mb-2">
            <strong className="text-amber-400">ElchmE's Blue → Red → Green → Gold flow</strong> is a disciplined pause from blame that turns friction (or appreciation) into forward motion.
          </p>
          <p className="text-white/60 text-xs leading-relaxed">
            We speak from lived experience, listen without defense, name what matters, and make clean requests that invite choice—not compliance. That's the whole engine.
          </p>
        </div>

        {/* BLUE - Clear Seeing */}
        <div className="mb-4 p-4 bg-blue-900/10 rounded-lg border border-blue-500/20">
          <div className="flex items-center gap-2 mb-3">
            <ObserveIcon size={20} color="#60A5FA" />
            <h3 className="text-blue-300 font-semibold">BLUE — Clear Seeing (Observation)</h3>
          </div>
          <p className="text-white/80 text-sm leading-relaxed mb-2">
            For now, we set down blame, criticism, and old stories. Even if we're mostly right, we pause. <em>This pause is power.</em>
          </p>
          <ul className="space-y-1 text-white/60 text-xs ml-4">
            <li>• I name what I notice—what I see, hear, remember, or imagine—without judging it.</li>
            <li>• I focus on what supports or undermines well-being (mine, yours, or the relationship).</li>
            <li>• I stay curious about how you might be seeing this—what you notice that helps or hurts.</li>
          </ul>
          <p className="text-blue-200/70 text-xs mt-2 italic">This is not denial. It's precision.</p>
        </div>

        {/* RED - Honest Feeling */}
        <div className="mb-4 p-4 bg-red-900/10 rounded-lg border border-red-500/20">
          <div className="flex items-center gap-2 mb-3">
            <FeelIcon size={20} color="#F87171" />
            <h3 className="text-red-300 font-semibold">RED — Honest Feeling (Emotion & Sensation)</h3>
          </div>
          <p className="text-white/80 text-sm leading-relaxed mb-2">
            Now I check the body, not the courtroom.
          </p>
          <ul className="space-y-1 text-white/60 text-xs ml-4">
            <li>• I name emotions or physical sensations, not thoughts or conclusions.</li>
            <li>• I notice how I feel in response to what I observe.</li>
            <li>• I wonder—without assuming—how you might feel in response to what you observe.</li>
          </ul>
          <p className="text-red-200/70 text-xs mt-2 italic">Feelings are data, not weapons.</p>
        </div>

        {/* GREEN - What Matters */}
        <div className="mb-4 p-4 bg-emerald-900/10 rounded-lg border border-emerald-500/20">
          <div className="flex items-center gap-2 mb-3">
            <NeedIcon size={20} color="#34D399" />
            <h3 className="text-emerald-300 font-semibold">GREEN — What Matters (Needs & Values)</h3>
          </div>
          <p className="text-white/80 text-sm leading-relaxed mb-2">
            We go underneath strategies and preferences.
          </p>
          <ul className="space-y-1 text-white/60 text-xs ml-4">
            <li>• I name what I value or need that's giving rise to these feelings.</li>
            <li>• Not a demand. Not a specific behavior.</li>
            <li>• I listen for what you value or need, even if our strategies differ.</li>
          </ul>
          <p className="text-emerald-200/70 text-xs mt-2 italic">Needs don't compete; strategies do.</p>
        </div>

        {/* GOLD - Clean Ask */}
        <div className="mb-4 p-4 bg-amber-900/10 rounded-lg border border-amber-500/20">
          <div className="flex items-center gap-2 mb-3">
            <StarIcon size={20} color="#FBBF24" />
            <h3 className="text-amber-300 font-semibold">GOLD — Clean Ask (Requests & Invitations)</h3>
          </div>
          <p className="text-white/80 text-sm leading-relaxed mb-2">
            Now we move toward life.
          </p>
          <ul className="space-y-1 text-white/60 text-xs ml-4">
            <li>• I make a clear, concrete request—an action that could enrich my life—without demanding.</li>
            <li>• I receive your request the same way: as information, not pressure.</li>
            <li>• We adjust, negotiate, or try experiments—together.</li>
          </ul>
          <p className="text-amber-200/70 text-xs mt-2 italic">This is where new realities get built.</p>
        </div>

        {/* What This Creates */}
        <div className="mt-6 p-4 bg-gradient-to-br from-amber-900/20 to-rose-900/10 rounded-xl border border-amber-500/30">
          <h3 className="text-amber-300 font-semibold mb-3 text-center">What This Creates</h3>
          <p className="text-white/80 text-sm leading-relaxed text-center">
            A shared field where blame dissolves, understanding accelerates, and solutions appear that were invisible before. 
            Conflict becomes compost. Appreciation becomes clearer. Trust grows.
          </p>
          <p className="text-amber-200/70 text-xs mt-3 text-center italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            That's ElchmE: observe cleanly, feel honestly, name what matters, and invite action—again and again, until gold shows up.
          </p>
        </div>

        {/* Power Dynamics Reminder */}
        <div className="mt-4 p-3 bg-purple-900/20 rounded-lg border border-purple-500/20">
          <p className="text-purple-200/80 text-xs leading-relaxed">
            <strong>Remember:</strong> This works best between equals. If power imbalance exists, seek to level the field first—whether you hold more or less power, bring yourself to equal ground for connection to flow.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OFNRProcessHelper;
